import { zodResolver } from '@hookform/resolvers/zod';
import { AlertCircle, Lock } from 'lucide-react';
import { type ReactNode, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { z } from 'zod';
import {
  type AuthApp,
  type BetterAuthClient,
  type SocialSignInProvider,
  signInWithSocialProvider,
} from '../../lib/betterAuthClient';
import { inviteAuthPath } from '../../lib/invite-links';
import { Alert, AlertDescription } from '../../ui/alert';
import { Button } from '../../ui/button';
import { PasswordInput } from '../../ui/password-input';
import { AgenticFlowByline } from '../agentic-flow-byline';
import { BrandLockup } from '../brand-lockup';
import { SocialSignInButtons } from './SocialSignInButtons';
import {
  resetTurnstile,
  TURNSTILE_SIGNUP_ACTION,
  TURNSTILE_SITE_KEY,
  TurnstileWidget,
} from './TurnstileWidget';
import { useInviteSignup } from './useInviteSignup';
import { useSocialSignInProviders } from './useSocialSignInProviders';

const registerSchema = z
  .object({
    email: z.string().email('Invalid email address'),
    password: z.string().min(6, 'Password must be at least 6 characters'),
    confirmPassword: z.string().min(1, 'Please re-enter your password'),
  })
  .refine((values) => values.password === values.confirmPassword, {
    message: 'Passwords do not match',
    path: ['confirmPassword'],
  });

type RegisterFormValues = z.infer<typeof registerSchema>;

export type RegisterPageProps = {
  brandName: string;
  /** The product's mark, rendered in the brand lockup above the card. */
  logo: ReactNode;
  /** Which Better Auth instance this portal talks to (`/api/auth/{app}`). */
  app: AuthApp;
  authClient: BetterAuthClient;
  /**
   * Overrides the shared `TURNSTILE_SITE_KEY` when an environment runs its own widget. Portals
   * normally omit this — the default key covers every environment, and the backend gate stays
   * inert until `TURNSTILE_SECRET_KEY` is set, so both halves must be configured before anything
   * is enforced.
   */
  turnstileSiteKey?: string;
};

/**
 * Shared sign-up form for both portals: email + password + confirmation, or Google.
 *
 * When reached from an invite link (`?invite=<token>`) the email is fixed to the invited
 * address and read-only, and the token is passed to both sign-up paths so the backend can
 * reject a mismatched identity — the Google picker being the case the locked field can't
 * cover — before an account is created. See backend/src/auth/inviteSignupGuard.ts.
 */
export function RegisterPage({
  brandName,
  logo,
  app,
  authClient,
  turnstileSiteKey,
}: RegisterPageProps) {
  const [searchParams] = useSearchParams();
  const redirect = searchParams.get('redirect');
  const invite = useInviteSignup();
  const socialProviders = useSocialSignInProviders(app);
  const [error, setError] = useState<string | null>(
    searchParams.get('error_description') || (searchParams.get('error') ? 'Sign-up failed.' : null)
  );
  const [isLoading, setIsLoading] = useState(false);
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null);
  const navigate = useNavigate();

  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm<RegisterFormValues>({
    resolver: zodResolver(registerSchema),
  });

  useEffect(() => {
    if (invite.email) setValue('email', invite.email);
  }, [invite.email, setValue]);

  const onSubmit = async (data: RegisterFormValues) => {
    setError(null);
    setIsLoading(true);

    try {
      const { error: signUpError } = await authClient.signUp.email({
        email: invite.email || data.email,
        password: data.password,
        // No dedicated name field in this form — Better
        // Auth's user table does, so default it from the email local-part.
        name: (invite.email || data.email).split('@')[0],
        ...(invite.token ? { inviteToken: invite.token } : {}),
        // Field name is fixed by Cloudflare; the backend redeems it in auth/turnstile.ts.
        ...(turnstileToken ? { 'cf-turnstile-response': turnstileToken } : {}),
      });

      if (signUpError) {
        // The token was redeemed (or rejected) by the backend and is now spent. Reissue a
        // challenge, or the user's next attempt fails verification through no fault of theirs.
        resetTurnstile();
        setTurnstileToken(null);
        setError(signUpError.message || 'Failed to create account. Please try again.');
        return;
      }

      navigate(redirect || '/dashboard');
    } catch (err: unknown) {
      console.error('Registration error:', err);
      const errorMessage =
        err instanceof Error ? err.message : 'Failed to create account. Please try again.';
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const onSocialSignIn = async (provider: SocialSignInProvider) => {
    setError(null);
    try {
      const returnTo = `${window.location.origin}/oauth-complete?redirect=${encodeURIComponent(redirect || '/dashboard')}`;
      await signInWithSocialProvider(authClient, app, provider, returnTo, {
        inviteToken: invite.token,
        loginHint: invite.email,
        // A rejected identity comes back here rather than the Worker's bare error page, so the
        // reason lands on the form the user was filling in.
        errorCallbackURL: `${window.location.origin}/register${window.location.search}`,
      });
    } catch (err: unknown) {
      const errorMessage = err instanceof Error ? err.message : 'Could not start that sign-up.';
      setError(errorMessage);
    }
  };

  const emailLocked = Boolean(invite.email);
  const awaitingInvite = invite.isLoading;

  return (
    <main className="min-h-screen flex flex-col">
      <div className="flex-1 flex items-center justify-center p-6">
        <div className="w-full max-w-md space-y-6">
          <BrandLockup brandName={brandName} logo={logo} />
          <div className="space-y-8 bg-card p-8 rounded-xl border border-border shadow-sm">
            <div className="text-center">
              <h2 className="text-2xl font-bold tracking-tight">Create an account</h2>
              <p className="text-sm text-muted-foreground mt-2">
                {invite.workspaceName
                  ? `Finish setting up your account to join ${invite.workspaceName}`
                  : `Get started with ${brandName}`}
              </p>
            </div>

            {invite.isUnusable && (
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>
                  That invitation is no longer valid — it may have expired or already been used. You
                  can still create an account, then ask for a new invite.
                </AlertDescription>
              </Alert>
            )}

            {error && (
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}

            <form className="space-y-4" onSubmit={handleSubmit(onSubmit)}>
              <div className="space-y-2">
                <label htmlFor="email" className="text-sm font-medium">
                  Email
                </label>
                <input
                  id="email"
                  type="email"
                  placeholder="name@example.com"
                  readOnly={emailLocked}
                  aria-readonly={emailLocked}
                  {...register('email')}
                  className={`w-full px-3 py-2 border border-border rounded-md bg-background focus:outline-none focus:ring-2 focus:ring-ring ${
                    emailLocked ? 'cursor-not-allowed text-muted-foreground' : ''
                  }`}
                />
                {emailLocked ? (
                  <p className="flex items-center gap-1.5 text-[10px] text-muted-foreground">
                    <Lock className="h-3 w-3 shrink-0" />
                    Your invitation was sent to this address, so the account has to use it.
                  </p>
                ) : (
                  errors.email && <p className="text-xs text-destructive">{errors.email.message}</p>
                )}
              </div>
              <div className="space-y-2">
                <label htmlFor="password" className="text-sm font-medium">
                  Password
                </label>
                <PasswordInput id="password" {...register('password')} />
                {errors.password && (
                  <p className="text-xs text-destructive">{errors.password.message}</p>
                )}
                <p className="text-[10px] text-muted-foreground">
                  Password must be at least 6 characters.
                </p>
              </div>
              <div className="space-y-2">
                <label htmlFor="confirmPassword" className="text-sm font-medium">
                  Confirm password
                </label>
                <PasswordInput id="confirmPassword" {...register('confirmPassword')} />
                {errors.confirmPassword && (
                  <p className="text-xs text-destructive">{errors.confirmPassword.message}</p>
                )}
              </div>
              <TurnstileWidget
                siteKey={turnstileSiteKey ?? TURNSTILE_SITE_KEY}
                action={TURNSTILE_SIGNUP_ACTION}
                onToken={setTurnstileToken}
                className="mt-4 flex justify-center"
              />
              <Button className="w-full mt-4" type="submit" disabled={isLoading || awaitingInvite}>
                {isLoading ? 'Creating Account...' : 'Sign Up'}
              </Button>
            </form>

            <div className="space-y-2">
              <SocialSignInButtons
                providers={socialProviders}
                action="Sign up"
                disabled={awaitingInvite}
                onSelect={onSocialSignIn}
              />
              {emailLocked && socialProviders.length > 0 && (
                <p className="text-center text-[10px] text-muted-foreground">
                  Choose the account for {invite.email} — any other is rejected without creating
                  anything.
                </p>
              )}
            </div>

            <div className="text-center text-sm">
              <span className="text-muted-foreground">Already have an account? </span>
              <button
                type="button"
                onClick={() =>
                  navigate(
                    invite.token
                      ? inviteAuthPath('login', invite.token)
                      : `/login${redirect ? `?redirect=${encodeURIComponent(redirect)}` : ''}`
                  )
                }
                className="font-medium text-primary hover:underline"
              >
                Sign in
              </button>
            </div>

            {emailLocked && (
              <div className="text-center text-xs">
                <button
                  type="button"
                  onClick={() => navigate('/register')}
                  className="text-muted-foreground hover:underline"
                >
                  Signing up as someone else? Create an unrelated account
                </button>
              </div>
            )}
          </div>
          <p className="text-center">
            <AgenticFlowByline />
          </p>
        </div>
      </div>
    </main>
  );
}
